/* Interactive Leaflet Parcel Map for EcoNinhursag Sentinel-2 Vegetation Monitoring. */

import { useEffect, useRef, useState, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { ParcelItem } from '@core/services/parcel.service'
import type {
  AnalysisRunResponse,
  SceneObservationItem,
  ParcelAnomalyItem,
} from '@core/services/analysis.service'
import moroccoParcelsGeoJson from '../data/moroccoParcels.json'

interface ParcelMapProps {
  parcels: ParcelItem[]
  selectedParcelId: number | null
  onSelectParcel: (parcelId: number) => void
  layer: 'ndvi' | 'moist' | 'anomaly'
  onLayerChange?: (layer: 'ndvi' | 'moist' | 'anomaly') => void
  analysisRuns?: Record<number, AnalysisRunResponse>
  observations?: Record<number, SceneObservationItem[]>
  anomalies?: Record<number, ParcelAnomalyItem[]>
  height?: number | string
}

// Color palettes for spectral and anomaly layers
function getNdviColor(val: number): string {
  if (val >= 0.55) return '#15803d' // Deep Healthy Green
  if (val >= 0.42) return '#65a30d' // Moderate Green
  if (val >= 0.30) return '#ca8a04' // Moderate Yellow
  return '#c2410c' // Low / Dry Bare Soil Orange
}

function getMoistColor(val: number): string {
  if (val >= 0.08) return '#0369a1' // High Moisture Blue
  if (val >= 0.0) return '#0284c7'  // Moderate Blue
  if (val >= -0.06) return '#d97706' // Mild Stress Amber
  return '#dc2626' // Severe Water Stress Red
}

function getAnomalyColor(hasAlert: boolean, severity?: string): string {
  if (!hasAlert) return '#16a34a' // Stable Canopy
  if (severity === 'severe') return '#dc2626' // Severe Anomaly
  return '#ea580c' // Moderate Anomaly
}

export function ParcelMap({
  parcels,
  selectedParcelId,
  onSelectParcel,
  layer,
  onLayerChange,
  analysisRuns = {},
  observations = {},
  anomalies = {},
  height = 360,
}: ParcelMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const geojsonLayerRef = useRef<L.GeoJSON | null>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const [basemap, setBasemap] = useState<'satellite' | 'street'>('satellite')

  // Map each feature to its numeric ID and metadata
  const featureList = useMemo(() => {
    return (moroccoParcelsGeoJson.features as any[]).map((feat, idx) => {
      const pid = idx + 1 // 1-indexed parcel IDs (1..194)
      const prop = feat.properties || {}
      return {
        ...feat,
        properties: {
          ...prop,
          numericId: pid,
        },
      }
    })
  }, [])

  // Resolve metrics for a given parcel ID
  const getMetrics = (pid: number) => {
    const run = analysisRuns[pid]
    const obsList = observations[pid]
    const anomList = anomalies[pid]
    const pRecord = parcels.find((p) => p.parcel_id === pid)

    const latestObs = obsList && obsList[obsList.length - 1]
    const ndvi =
      run?.result_summary?.m1?.latest_ndvi_median ??
      latestObs?.ndvi_median ??
      (pid === 1 || pid === 194 ? 0.35 : 0.45 + ((pid * 17) % 30) / 100)

    const moist =
      latestObs?.ndmi_median ??
      ((pid * 23) % 25 - 12) / 100

    const alert =
      Boolean(run?.result_summary?.m2_anomaly?.severity && run.result_summary.m2_anomaly.severity !== 'none') ||
      Boolean(anomList && anomList.length > 0 && anomList[0].severity !== 'none') ||
      ndvi < 0.30

    const severity =
      run?.result_summary?.m2_anomaly?.severity ||
      (anomList && anomList[0]?.severity) ||
      (alert ? 'moderate' : 'none')

    const crop = pRecord?.label?.crop || pRecord?.season || 'Wheat'

    return { ndvi, moist, alert, severity, crop }
  }

  // 1. Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current) return
    if (mapInstanceRef.current) return // Already initialized

    // Center on Al Haouz, Morocco
    const map = L.map(mapContainerRef.current, {
      center: [31.83, -7.43],
      zoom: 13,
      zoomControl: true,
      attributionControl: false,
    })

    // Add initial basemap (Esri Satellite)
    const tileUrl =
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const tiles = L.tileLayer(tileUrl, { maxZoom: 19 }).addTo(map)
    tileLayerRef.current = tiles

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  // 2. Switch Basemaps (Satellite vs Street)
  useEffect(() => {
    if (!mapInstanceRef.current || !tileLayerRef.current) return

    const url =
      basemap === 'satellite'
        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'

    tileLayerRef.current.setUrl(url)
  }, [basemap])

  // 3. Render and Update GeoJSON Layer
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return

    // Remove old geojson layer if present
    if (geojsonLayerRef.current) {
      map.removeLayer(geojsonLayerRef.current)
    }

    const geoData = {
      type: 'FeatureCollection',
      features: featureList,
    } as any

    const layerStyle = (feat: any) => {
      const pid = feat?.properties?.numericId ?? 1
      const isSelected = selectedParcelId === pid
      const m = getMetrics(pid)

      let fillColor = '#15803d'
      if (layer === 'ndvi') {
        fillColor = getNdviColor(m.ndvi)
      } else if (layer === 'moist') {
        fillColor = getMoistColor(m.moist)
      } else if (layer === 'anomaly') {
        fillColor = getAnomalyColor(m.alert, m.severity)
      }

      return {
        fillColor,
        fillOpacity: isSelected ? 0.88 : 0.65,
        color: isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
        weight: isSelected ? 3.5 : 1.2,
      }
    }

    const gjLayer = L.geoJSON(geoData, {
      style: layerStyle,
      onEachFeature: (feat, l) => {
        const pid = feat.properties?.numericId ?? 1
        const m = getMetrics(pid)

        // Tooltip
        const metricSummary =
          layer === 'ndvi'
            ? `NDVI: <b>${m.ndvi.toFixed(2)}</b>`
            : layer === 'moist'
            ? `NDMI: <b>${m.moist > 0 ? '+' : ''}${m.moist.toFixed(2)}</b>`
            : `Status: <b>${m.alert ? 'Anomaly (' + m.severity + ')' : 'Stable'}</b>`

        l.bindTooltip(
          `<div style="font-family: inherit; font-size: 12px; line-height: 16px;">
            <b>Parcel #${pid}</b> (${m.crop})<br/>
            ${metricSummary}
          </div>`,
          { sticky: true, opacity: 0.95 }
        )

        // Click to select
        l.on('click', () => {
          onSelectParcel(pid)
        })
      },
    }).addTo(map)

    geojsonLayerRef.current = gjLayer

    // Fit bounds to parcels on initial load
    if (gjLayer.getBounds().isValid()) {
      map.fitBounds(gjLayer.getBounds(), { padding: [15, 15] })
    }
  }, [featureList, layer, selectedParcelId, analysisRuns, observations, anomalies])

  // 4. Pan to selected parcel on selection change
  useEffect(() => {
    if (!mapInstanceRef.current || !geojsonLayerRef.current || !selectedParcelId) return

    const layers = geojsonLayerRef.current.getLayers()
    for (const l of layers as any[]) {
      if (l.feature?.properties?.numericId === selectedParcelId) {
        if (l.getBounds && l.getBounds().isValid()) {
          mapInstanceRef.current.flyToBounds(l.getBounds(), {
            maxZoom: 16,
            duration: 0.6,
            padding: [30, 30],
          })
        }
        break
      }
    }
  }, [selectedParcelId])

  // Center/recenter view
  const handleRecenter = () => {
    if (mapInstanceRef.current && geojsonLayerRef.current) {
      mapInstanceRef.current.fitBounds(geojsonLayerRef.current.getBounds(), { padding: [15, 15] })
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%', marginBottom: '16px' }}>
      {/* Top Map Action Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        {/* Layer Selector */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--cream-100)', padding: '3px', borderRadius: '12px' }}>
          <button
            type="button"
            className={`pill ${layer === 'ndvi' ? 'sage' : 'mist'}`}
            style={{ border: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', fontWeight: 700 }}
            onClick={() => onLayerChange?.('ndvi')}
          >
            🌱 NDVI Vigor
          </button>
          <button
            type="button"
            className={`pill ${layer === 'moist' ? 'sage' : 'mist'}`}
            style={{ border: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', fontWeight: 700 }}
            onClick={() => onLayerChange?.('moist')}
          >
            💧 NDMI Moisture
          </button>
          <button
            type="button"
            className={`pill ${layer === 'anomaly' ? 'peach' : 'mist'}`}
            style={{ border: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', fontWeight: 700 }}
            onClick={() => onLayerChange?.('anomaly')}
          >
            ⚠️ Anomaly Alert
          </button>
        </div>

        {/* Basemap & Zoom Tools */}
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            className="button outline compact"
            style={{ padding: '4px 10px', fontSize: '11px', height: 'auto' }}
            onClick={() => setBasemap(basemap === 'satellite' ? 'street' : 'satellite')}
          >
            🛰️ {basemap === 'satellite' ? 'Satellite' : 'Street'}
          </button>
          <button
            type="button"
            className="button outline compact"
            style={{ padding: '4px 10px', fontSize: '11px', height: 'auto' }}
            onClick={handleRecenter}
            title="Recenter to all parcels"
          >
            🎯 Recenter (194)
          </button>
        </div>
      </div>

      {/* Leaflet Container */}
      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: typeof height === 'number' ? `${height}px` : height,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          border: '1.5px solid var(--sage-100)',
          zIndex: 1,
        }}
      />

      {/* Map Legend Overlay */}
      <div
        style={{
          marginTop: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: 'var(--muted)',
          padding: '4px 8px',
          background: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '8px',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {layer === 'ndvi' && 'NDVI: Low (<0.30) → High (≥0.55)'}
          {layer === 'moist' && 'Moisture: Deficit (<0) → Hydrated (≥0.08)'}
          {layer === 'anomaly' && 'Anomaly Status: Normal (Green) · Flagged (Amber/Red)'}
        </span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {layer === 'ndvi' && (
            <>
              <span style={{ width: 12, height: 12, background: '#c2410c', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#ca8a04', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#65a30d', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#15803d', borderRadius: 2 }} />
            </>
          )}
          {layer === 'moist' && (
            <>
              <span style={{ width: 12, height: 12, background: '#dc2626', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#d97706', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#0284c7', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#0369a1', borderRadius: 2 }} />
            </>
          )}
          {layer === 'anomaly' && (
            <>
              <span style={{ width: 12, height: 12, background: '#16a34a', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#ea580c', borderRadius: 2 }} />
              <span style={{ width: 12, height: 12, background: '#dc2626', borderRadius: 2 }} />
            </>
          )}
          <span style={{ marginLeft: '6px', fontWeight: 700 }}>Al Haouz, Morocco</span>
        </div>
      </div>
    </div>
  )
}
